import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { components } from "react-select";
import { useAuth } from "../../../firebase/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import ActionSubType from "../../../interfaces/task/taskActions/ActionSubType";
import TaskActionRequest from "../../../interfaces/task/taskActions/TaskActionRequest";
import {
  createTaskAction,
  getTaskActionSubTypes,
} from "../../../services/taskServices/TaskActionService";
import {
  getTemplateFiles,
  uploadFile,
} from "../../../services/fileServices/FileService";
import TemplateFile from "../../../interfaces/task/TaskFiles/TemplateFile";
import { getTasks } from "../../../services/taskServices/TaskService";
import TaskList from "../../../interfaces/task/TaskList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardUser, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button, Spinner } from "react-bootstrap";
import "./CreateTaskActivityComponent.css";
import { toast } from "react-toastify";

interface OptionType {
  value: string;
  label: string;
}

interface CreateTaskActivityComponentProps {
  taskIdFromProp?: string;
  onActivityCreated?: () => void;
}

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div className="search_by_task_id">
        <h2>
          <strong>
            {"("}
            {props.data.taskId}
            {")"}
          </strong>{" "}
          {props.data.title}
        </h2>
        <div className="search_by_task_id_desc">
          <p>
            <FontAwesomeIcon icon={faClipboardUser} />
            {props.data.participantName}
          </p>
        </div>
      </div>
    </components.Option>
  );
};

const CreateTaskActivityComponent: React.FC<
  CreateTaskActivityComponentProps
> = ({ taskIdFromProp, onActivityCreated }) => {
  const { currentUser, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [actionTypes, setActionTypes] = useState<ActionSubType[] | null>(null);
  const [actionTypeOptions, setActionTypeOptions] = useState<OptionType[]>([]);
  const [taskOptions, setTaskOptions] = useState<OptionType[]>([]);
  const [taskId, setTaskId] = useState<string>("");
  const [fetchedTasks, setFetchedTasks] = useState<TaskList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [attachmentRequirements, setAttachmentRequirements] =
    useState<TemplateFile[]>();
  const [selectedAttachment, setSelectedAttachment] = useState<
    TemplateFile | undefined
  >(undefined);

  const [recommendedTimeImmutable, setRecommendedTimeImmutable] =
    useState<boolean>(false);
  const [descriptionImmutable, setDescriptionImmutable] =
    useState<boolean>(false);

  // New states for Hours and Minutes
  const [hours, setHours] = useState<string>("0");
  const [minutes, setMinutes] = useState<string>("15");

  // New state for file upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const labelsToFilterOut = [
    "COMMENT",
    "TASK FILE DELETED",
    "FILE UPLOAD",
    "TASK APPROVAL",
    "TASK SUBMISSION",
    "TASK COMPLETED",
    "REJECTED",
  ];

  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");

  const initialCompletionDate = `${year}-${month}-${day}`;

  const currentHour = String(currentDate.getHours()).padStart(2, "0");
  const currentMinute = String(currentDate.getMinutes()).padStart(2, "0");
  const initialCompletionTime = `${currentHour}:${currentMinute}`;

  const [newAction, setNewAction] = useState<TaskActionRequest>({
    taskId: "",
    description: "",
    subTypeId: "",
    durationInMinutes: "15",
    completionDate: initialCompletionDate,
    completionTime: initialCompletionTime,
    requiresFile: null,
  });

  const [isModal, setIsModal] = useState<boolean>(
    taskIdFromProp ? true : false
  );

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const handleTaskIdChange = (selectedOption: SingleValue<OptionType>) => {
    const taskId = selectedOption ? selectedOption.value : "";
    setTaskId(taskId);
    setNewAction((prev) => ({ ...prev, taskId }));
    if (!isModal) {
      navigate(`/addActivity/new/activity?taskId=${taskId}`);
    }

    // Clear error when user interacts
    if (formErrors.taskId) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.taskId;
        return newErrors;
      });
    }
  };

  useEffect(() => {
    let idForState: string | null = null;
    let numericIdForAction: string | null = null;

    if (taskIdFromProp) {
      idForState = taskIdFromProp;
      numericIdForAction = taskIdFromProp.includes("-")
        ? taskIdFromProp.split("-")[1]
        : taskIdFromProp;
    } else {
      const searchParams = new URLSearchParams(location.search);
      const taskIdFromUrl = searchParams.get("taskId");
      if (taskIdFromUrl) {
        idForState = taskIdFromUrl;
        numericIdForAction = taskIdFromUrl;
      }
    }

    if (idForState && numericIdForAction) {
      setTaskId(idForState);
      setNewAction((prev) => ({ ...prev, taskId: numericIdForAction! }));
    }
  }, [location, taskIdFromProp]);

  useEffect(() => {
    if (!actionTypes && currentUser) {
      const getActionTypes = async () => {
        const authToken = await currentUser.getIdToken();
        const retrievedTypes = await getTaskActionSubTypes(authToken);
        if (retrievedTypes) {
          const filteredOptions = retrievedTypes.filter(
            (option) => !labelsToFilterOut.includes(option.actionSubTypeName)
          );
          setActionTypes(filteredOptions);
        }
      };
      getActionTypes();
    }
  }, [currentUser, actionTypes]);

  useEffect(() => {
    if (actionTypes && currentUser) {
      const fetchTaskAttachmentRequirements = async () => {
        if (currentUser) {
          const authToken: string = await currentUser.getIdToken();
          const fetchedAttachmentRequirements = await getTemplateFiles(
            authToken
          );
          if (fetchedAttachmentRequirements) {
            setAttachmentRequirements(fetchedAttachmentRequirements);
          }
        }
      };

      fetchTaskAttachmentRequirements();
    }
  }, [actionTypes, currentUser]);

  useEffect(() => {
    if (currentUser) {
      const fetchTasks = async () => {
        if (currentUser) {
          const authToken: string = await currentUser.getIdToken();
          const fetchTasksForUser = await getTasks(authToken);
          if (fetchTasksForUser) {
            setFetchedTasks(fetchTasksForUser.tasks);
          }
        }
      };

      fetchTasks();
    }
  }, [currentUser]);

  useEffect(() => {
    if (actionTypes && actionTypes.length > 0) {
      const formattedActionTypes = actionTypes.map((at) => ({
        value: at.id.toString(),
        label: at.actionSubTypeName,
      }));
      setActionTypeOptions(formattedActionTypes);
    }

    if (fetchedTasks && fetchedTasks.length > 0) {
      const formattedTasks = fetchedTasks.map((task) => ({
        value: task.id.toString(),
        label: `(${currentOrganization?.abbreviation}-${task.id}) ${task.title} - ${task.participant?.firstName} ${task.participant?.lastName}`,
        taskId: currentOrganization?.abbreviation + "-" + task.id,
        title: task.title,
        participantName: `${task.participant?.firstName} ${task.participant?.lastName}`,
      }));
      setTaskOptions(formattedTasks);
    }
  }, [actionTypes, fetchedTasks, currentOrganization]);

  const handleActionTypeChange = (
    selectedActionType: SingleValue<OptionType>
  ) => {
    const subTypeId = selectedActionType ? selectedActionType.value : "";
    const currentNewAction = { ...newAction };
    currentNewAction.subTypeId = subTypeId;

    const actionSubType = actionTypes?.filter(
      (at) => at.id === parseInt(subTypeId, 10)
    )[0];

    if (actionSubType?.description) {
      setDescriptionImmutable(true);
      currentNewAction.description = actionSubType.description;
    } else {
      setDescriptionImmutable(false);
      currentNewAction.description = "";
    }

    if (actionSubType?.recommendedTimeInMinutes) {
      setRecommendedTimeImmutable(true);
      const recommendedMinutes = actionSubType.recommendedTimeInMinutes;
      // Convert recommended minutes to hours and minutes
      const hrs = Math.floor(recommendedMinutes / 60);
      const mins = recommendedMinutes % 60;
      setHours(hrs.toString());
      setMinutes(mins.toString());
      currentNewAction.durationInMinutes = recommendedMinutes.toString();
    } else {
      setRecommendedTimeImmutable(false);
      setHours("0");
      setMinutes("15");
      currentNewAction.durationInMinutes = "15";
    }

    if (actionSubType?.requiresFile) {
      currentNewAction.requiresFile = actionSubType.requiresFile.toString();
      updateRequiredFile(actionSubType.requiresFile.toString());
    } else {
      currentNewAction.requiresFile = "";
      setSelectedAttachment(undefined);
      setSelectedFile(null);
    }

    setNewAction(currentNewAction);

    // Clear error when user interacts
    if (formErrors.subTypeId) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.subTypeId;
        return newErrors;
      });
    }
  };

  const updateRequiredFile = (requiresFile: string) => {
    setSelectedAttachment(
      attachmentRequirements?.find(
        (attachment) => attachment.id === parseInt(requiresFile)
      )
    );
  };

  // Handle Hours change - exactly like LogActivityModal
  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow empty string or positive integers
    if (value === "" || /^\d+$/.test(value)) {
      setHours(value);
      const totalMinutes =
        (parseInt(value) || 0) * 60 + (parseInt(minutes) || 0);
      setNewAction((prev) => ({
        ...prev,
        durationInMinutes: totalMinutes.toString(),
      }));

      if (formErrors.timeSpent) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.timeSpent;
          return newErrors;
        });
      }
    }
  };

  // Handle Minutes change - exactly like LogActivityModal
  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or positive integers less than 60
    if (
      value === "" ||
      (/^\d+$/.test(value) && (value === "" || parseInt(value) < 60))
    ) {
      setMinutes(value);
      const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(value) || 0);
      setNewAction((prev) => ({
        ...prev,
        durationInMinutes: totalMinutes.toString(),
      }));

      if (formErrors.timeSpent) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.timeSpent;
          return newErrors;
        });
      }
    }
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
    if (formErrors.attachment) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.attachment;
        return newErrors;
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setNewAction({ ...newAction, [name]: value });

    // Clear errors on change
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAction({ ...newAction, completionDate: e.target.value });
    if (formErrors.completionDate) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.completionDate;
        return newErrors;
      });
    }
  };

  const handleChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAction({ ...newAction, completionTime: e.target.value });
    if (formErrors.completionTime) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.completionTime;
        return newErrors;
      });
    }
  };

  const validateFields = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!newAction.taskId) errors.taskId = "Task is required.";
    if (!newAction.subTypeId) errors.subTypeId = "Activity type is required.";

    // Validate hours and minutes - exactly like LogActivityModal
    if (
      (!hours || parseInt(hours) === 0) &&
      (!minutes || parseInt(minutes) === 0)
    ) {
      errors.timeSpent = "Time spent is required.";
    }
    if (hours && isNaN(Number(hours)))
      errors.timeSpent = "Hours must be a valid number.";
    if (minutes && isNaN(Number(minutes)))
      errors.timeSpent = "Minutes must be a valid number.";
    if (Number(minutes) >= 60)
      errors.timeSpent = "Minutes must be less than 60.";

    if (!newAction.completionDate)
      errors.completionDate = "Completion date is required.";
    if (!newAction.completionTime)
      errors.completionTime = "Completion time is required.";
    if (!newAction.description) errors.description = "Description is required.";

    // Validate attachment if required
    if (selectedAttachment && !selectedFile) {
      errors.attachment = "Please select a file to upload";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement> | undefined = undefined
  ) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateFields()) {
      toast.error("Please fix validation errors before submitting");
      return;
    }

    if (!currentUser) {
      toast.error("Authentication error. Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const authToken: string = await currentUser.getIdToken();

      // If attachment is required, upload the file first
      if (selectedFile) {
        const fileToUpload = selectedAttachment
          ? new File(
              [selectedFile],
              selectedAttachment.fileName, // required → forced name
              { type: selectedFile.type }
            )
          : selectedFile; // optional → original name

        const fileSize = fileToUpload.size;
        const mimeType = fileToUpload.type || "application/octet-stream";

        const uploadSuccess = await uploadFile(
          fileToUpload,
          newAction.taskId,
          mimeType,
          fileSize,
          authToken
        );

        if (!uploadSuccess) {
          toast.error("File upload failed");
          setIsLoading(false);
          return;
        }
      }

      // Submit action
      const result = await createTaskAction(authToken, newAction);

      if (result) {
        toast.success("Activity logged successfully!");
        if (onActivityCreated) {
          onActivityCreated();
        } else {
          navigate(
            `/task/${currentOrganization?.abbreviation}-${newAction.taskId}?focusedActivity=${result}`
          );
        }
      } else {
        toast.error("Failed to log activity");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to log activity. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="modal-activity-reject-container">
        <div className="modal-activity-reject-header">
          <h2 className="modal-activity-reject-title">Add Activity</h2>
          <button
            className="modal-activity-reject-close rounded-[0.125rem] opacity-[0.7] w-[1rem] h-[1rem]"
            onClick={(e) => {
              e.preventDefault();
              onActivityCreated && onActivityCreated();
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="w-[1rem] h-[1rem]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-activity-reject-form">
          <div className="modal-activity-reject-body">
            {/* <div className="form-group">
              <label className="form_labels">Task By ID</label>
              <Select
                options={taskOptions}
                value={taskOptions.find(
                  (option) => option.value === newAction.taskId
                )}
                onChange={handleTaskIdChange}
                components={{ Option: CustomOption }}
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={isModal}
                placeholder="Select"
              />
              {formErrors.taskId && (
                <span className="error-message">{formErrors.taskId}</span>
              )}
            </div> */}

            <div className="form-group !mb-[1.25rem]">
              <label className="form_labels !mt-[0.5rem]">
                Activity Type<span className="required">*</span>
              </label>
              <Select
                options={actionTypeOptions}
                value={actionTypeOptions.find(
                  (option) => option.value === newAction.subTypeId
                )}
                onChange={handleActionTypeChange}
                className="react-select-container  !text-[1rem]"
                classNamePrefix="react-select !text-[1rem] "
                placeholder="Select activity type"
                components={{
                  IndicatorSeparator: () => null,
                }}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: "#F3F3F5",
                    borderRadius: "0.5rem",
                    borderColor: "transparent",
                    boxShadow: "none",
                    height: "2.75rem",
                    "&:hover": {
                      borderColor: "transparent",
                    },
                  }),
                }}
              />
              {formErrors.subTypeId && (
                <span className="error-message">{formErrors.subTypeId}</span>
              )}
            </div>

            {/* Hours and Minutes Row - exactly like LogActivityModal */}
            <div className="form-row !mb-0">
              <div className="form-group form-group-half">
                <label htmlFor="hours" className="form_labels">
                  Hours<span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="hours"
                  name="hours"
                  value={hours}
                  onChange={handleHoursChange}
                  placeholder="0"
                  className="form-input h-[2.75rem] !bg-[#F3F3F5] !text-[#717182]"
                />
                {formErrors.timeSpent && (
                  <span className="error-message">{formErrors.timeSpent}</span>
                )}
              </div>

              <div className="form-group form-group-half">
                <label htmlFor="minutes" className="form_labels">
                  Minutes<span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="minutes"
                  name="minutes"
                  value={minutes}
                  onChange={handleMinutesChange}
                  placeholder="0"
                  className="form-input h-[2.75rem] !bg-[#F3F3F5] !text-[#717182]"
                />
                {formErrors.timeSpent && (
                  <span className="error-message">{formErrors.timeSpent}</span>
                )}
              </div>
            </div>

            <div className="form-row !mb-0">
              <div className="form-group form-group-half">
                <label htmlFor="completionDate" className="form_labels">
                  Completion Date<span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="completionDate"
                  name="completionDate"
                  value={newAction.completionDate}
                  onChange={handleChangeDate}
                  className="form-input h-[2.75rem] !bg-[#F3F3F5] !text-[#717182]"
                />
                {formErrors.completionDate && (
                  <span className="error-message">
                    {formErrors.completionDate}
                  </span>
                )}
              </div>

              <div className="form-group form-group-half">
                <label htmlFor="completionTime" className="form_labels">
                  Completion Time<span className="required">*</span>
                </label>
                <input
                  type="time"
                  id="completionTime"
                  name="completionTime"
                  value={newAction.completionTime}
                  onChange={handleChangeTime}
                  className="form-input h-[2.75rem] !bg-[#F3F3F5] !text-[#717182]"
                />
                {formErrors.completionTime && (
                  <span className="error-message">
                    {formErrors.completionTime}
                  </span>
                )}
              </div>
            </div>

            <div className="form-group !mb-[1.4rem]">
              <label htmlFor="description" className="form_labels">
                Work Done<span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                placeholder="Describe the work completed or progress made..."
                value={newAction.description}
                onChange={handleChange}
                className="form-textarea !max-h[4rem] !bg-[#F3F3F5] !text-[#717182]"
                rows={4}
              />
              {formErrors.description && (
                <span className="error-message">{formErrors.description}</span>
              )}
            </div>

            {/* Attachment Section - exactly like LogActivityModal */}
            <div className="form-group">
              <span
                className="form-label-attachment"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "2px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  style={{ width: "20px", height: "20px", marginBottom: "2px" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                  />
                </svg>
                <span className="!text-[1rem]">Attachment </span>
                {selectedAttachment && <span className="required">*</span>}
              </span>

              <label
                htmlFor="fileUpload"
                className="form-input !h-[3rem]"
                style={{
                  border: formErrors.attachment
                    ? "1px dashed red"
                    : "1px dashed #e5e5e5",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                  gap: "8px",
                  color: "#717182",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  style={{ width: "24px", height: "24px", color: "#C9C9C9" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M18.375 12.739l-7.5 7.5a4.125 4.125 0 01-5.836-5.836l7.5-7.5a2.75 2.75 0 013.889 3.889l-7.5 7.5a1.375 1.375 0 01-1.945-1.945l7.5-7.5"
                  />
                </svg>
                <span>
                  {selectedFile ? selectedFile.name : "Choose a file"}
                </span>
              </label>

              <input
                id="fileUpload"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf"
                onChange={handleFileChange}
              />

              {selectedAttachment && (
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: "14px",
                    color: "#6B7280",
                  }}
                >
                  Required File:{" "}
                  <span
                    style={{
                      backgroundColor: "#F3F4F6",
                      padding: "2px 6px",
                      fontStyle: "italic",
                    }}
                  >
                    {selectedAttachment.fileName}
                  </span>
                </div>
              )}

              {formErrors.attachment && (
                <span className="error-message">{formErrors.attachment}</span>
              )}
            </div>
          </div>

          <div className="modal-activity-reject-footer">
            <button
              type="button"
              className="btn btn-secondary cancel-btn"
              onClick={(e) => {
                e.preventDefault();
                onActivityCreated && onActivityCreated();
              }}
            >
              Cancel
            </button>
            {isLoading ? (
              <button
                type="button"
                className="btn btn-primary submit-btn"
                disabled
              >
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="btn-spinner"
                />
                Logging...
              </button>
            ) : (
              <button type="submit" className="btn btn-primary submit-btn">
                Log Activity
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateTaskActivityComponent;
